import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType, BodyType } from "./custom-fetch";

export interface UpvoteInput {
  email: string;
  comment?: string;
}

export interface UpvoteResult {
  id: number;
  issueId: number;
  comment: string | null;
  createdAt: string;
  newAffectedCount: number | null;
}

export interface IssueUpvote {
  id: number;
  issueId: number;
  email: string;
  comment: string | null;
  createdAt: string;
}

export const upvoteIssue = async (
  id: number,
  data: UpvoteInput,
  options?: RequestInit,
): Promise<UpvoteResult> => {
  return customFetch<UpvoteResult>(`/api/issues/${id}/upvote`, {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(data),
  });
};

export const getUpvoteIssueMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upvoteIssue>>,
    TError,
    { id: number; data: BodyType<UpvoteInput> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof upvoteIssue>>,
  TError,
  { id: number; data: BodyType<UpvoteInput> },
  TContext
> => {
  const mutationKey = ["upvoteIssue"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof upvoteIssue>>,
    { id: number; data: BodyType<UpvoteInput> }
  > = ({ id, data }) => upvoteIssue(id, data);

  return { mutationFn, ...mutationOptions };
};

export const useUpvoteIssue = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upvoteIssue>>,
    TError,
    { id: number; data: BodyType<UpvoteInput> },
    TContext
  >;
}): UseMutationResult<
  Awaited<ReturnType<typeof upvoteIssue>>,
  TError,
  { id: number; data: BodyType<UpvoteInput> },
  TContext
> => {
  return useMutation(getUpvoteIssueMutationOptions(options));
};

export interface IssuePublicComment {
  kind: "staff" | "carrier";
  comment: string;
  authorName: string | null;
  createdAt: string;
}

export const getIssuePublicCommentsUrl = (id: number) =>
  `/api/issues/${id}/comments`;

export const getIssuePublicComments = async (
  id: number,
  options?: RequestInit,
): Promise<IssuePublicComment[]> => {
  return customFetch<IssuePublicComment[]>(getIssuePublicCommentsUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetIssuePublicCommentsQueryOptions = <
  TData = Awaited<ReturnType<typeof getIssuePublicComments>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<
      Awaited<ReturnType<typeof getIssuePublicComments>>,
      TError,
      TData
    >>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getIssuePublicComments>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? [getIssuePublicCommentsUrl(id)];
  const queryFn = () => getIssuePublicComments(id);
  return { queryKey, queryFn, enabled: id > 0, ...queryOptions };
};

export const useGetIssuePublicComments = <
  TData = Awaited<ReturnType<typeof getIssuePublicComments>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<
      Awaited<ReturnType<typeof getIssuePublicComments>>,
      TError,
      TData
    >>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetIssuePublicCommentsQueryOptions(id, options);
  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };
  return { ...query, queryKey: queryOptions.queryKey };
};

export const getIssueUpvotesUrl = (id: number) =>
  `/api/admin/issues/${id}/upvotes`;

export const getIssueUpvotes = async (
  id: number,
  options?: RequestInit,
): Promise<IssueUpvote[]> => {
  return customFetch<IssueUpvote[]>(getIssueUpvotesUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetIssueUpvotesQueryOptions = <
  TData = Awaited<ReturnType<typeof getIssueUpvotes>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<
      Awaited<ReturnType<typeof getIssueUpvotes>>,
      TError,
      TData
    >>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getIssueUpvotes>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? [getIssueUpvotesUrl(id)];
  const queryFn = () => getIssueUpvotes(id);
  return { queryKey, queryFn, enabled: id > 0, ...queryOptions };
};

export const useGetIssueUpvotes = <
  TData = Awaited<ReturnType<typeof getIssueUpvotes>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<
      Awaited<ReturnType<typeof getIssueUpvotes>>,
      TError,
      TData
    >>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetIssueUpvotesQueryOptions(id, options);
  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };
  return { ...query, queryKey: queryOptions.queryKey };
};
